"use client";

import React, { useState, useEffect } from "react";
import dynamic from "next/dynamic";
import Image from "next/image";
import { motion, AnimatePresence, type Variants } from "framer-motion";
import { Star } from "lucide-react";

const DevSkillzBackground = dynamic(() => import("../ui/devskillzthree"), { ssr: false });

const reviews: {
  name: string;
  role: string;
  company: string;
  body: string;
  rating: number;
  avatar: string;
}[] = [
  {
    name: "Adebayo Olanrewaju",
    role: "Founder",
    company: "Lagos Logistics Hub",
    body: "Built our booking website from scratch. Fast, reliable, and exactly what we needed to get online reservations going without stress.",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1506277886164-e25aa3f4ef7f?w=80&h=80&fit=crop&crop=face",
  },
  {
    name: "Chika Nwosu",
    role: "Owner",
    company: "Glow by Chika",
    body: "Set up my online beauty store in record time. The checkout process is so smooth, and my sales have doubled since the redesign.",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=80&h=80&fit=crop&crop=face",
  },
  {
    name: "Sarah Jenkins",
    role: "Director",
    company: "SJ Boutique",
    body: "Professional, communicative, and technically brilliant. They took our outdated site and turned it into a modern, lead-generating machine.",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=80&h=80&fit=crop&crop=face",
  },
  {
    name: "Folake Akinyemi",
    role: "CEO",
    company: "Taste of Home",
    body: "I needed a fast website for my restaurant that allowed secure online ordering. The result was beautiful and incredibly easy for my staff to manage.",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1531384441138-2736e62e0919?w=80&h=80&fit=crop&crop=face",
  },
  {
    name: "Marcus Weaver",
    role: "Founder",
    company: "Urban Threads",
    body: "Really understood the brand vision. The e-commerce site is blazing fast, and the mobile experience is flawless. Best investment we made this year.",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&h=80&fit=crop&crop=face",
  },
  {
    name: "Emeka Okafor",
    role: "Managing Partner",
    company: "Okafor & Co",
    body: "Very clean code, highly secure backend, and a very sleek design. Handled all our specific business requirements without any fuss.",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1522529599102-193c0d76b5b6?w=80&h=80&fit=crop&crop=face",
  },
];

const containerVariants: Variants = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.1 },
  },
};

const cardVariants: Variants = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

const headingVariants: Variants = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

export default function Reviews() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setActive((prev) => (prev + 1) % reviews.length);
    }, 5000);
    return () => clearInterval(id);
  }, []);

  const slideVariants: Variants = {
    enter: { opacity: 0, x: 60 },
    center: { opacity: 1, x: 0, transition: { duration: 0.45, ease: "easeOut" } },
    exit: { opacity: 0, x: -60, transition: { duration: 0.3, ease: "easeIn" } },
  };

  return (
    <section className="relative w-full overflow-hidden py-20">
      {/* ── Three.js particle background ── */}
      <DevSkillzBackground className="absolute inset-0 h-full w-full opacity-30" />

      <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-8">
        <motion.div
          variants={headingVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="mb-14 text-center"
        >
          <h2 className="text-2xl font-bold tracking-tight text-[var(--foreground)] sm:text-3xl">
            What{" "}
            <span className="text-[var(--accent)]">clients say</span>
          </h2>
          <p className="mt-3 text-sm text-[var(--foreground)]/60 sm:text-base">
            Real feedback from real projects.
          </p>
        </motion.div>

        {/* ── Mobile carousel (hidden sm+) ── */}
        <div className="sm:hidden">
          <div className="relative h-64 overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                className="absolute inset-0 flex flex-col gap-4 rounded-2xl border border-[var(--foreground)]/8 bg-white/4 p-6 backdrop-blur-sm"
              >
                <div className="flex gap-0.5">
                  {Array.from({ length: reviews[active].rating }).map((_, i) => (
                    <Star key={i} size={15} className="fill-[var(--accent)] text-[var(--accent)]" />
                  ))}
                </div>
                <p className="flex-1 text-sm leading-relaxed text-[var(--foreground)]/80">
                  &ldquo;{reviews[active].body}&rdquo;
                </p>
                <div className="flex items-center gap-3 border-t border-[var(--foreground)]/8 pt-4">
                  <Image
                    src={reviews[active].avatar}
                    alt={reviews[active].name}
                    width={40}
                    height={40}
                    className="h-10 w-10 rounded-full object-cover"
                  />
                  <div>
                    <p className="text-sm font-semibold text-[var(--foreground)]">{reviews[active].name}</p>
                    <p className="text-xs text-[var(--foreground)]/50">
                      {reviews[active].role} · {reviews[active].company}
                    </p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Dots */}
          <div className="mt-5 flex justify-center gap-2">
            {reviews.map((_, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === active ? "w-6 bg-[var(--accent)]" : "w-1.5 bg-[var(--foreground)]/30"
                }`}
                aria-label={`Go to review ${i + 1}`}
              />
            ))}
          </div>
        </div>

        {/* ── Desktop grid (hidden on mobile) ── */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className="hidden sm:grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
        >
          {reviews.map((review) => (
            <motion.div
              key={review.name}
              variants={cardVariants}
              className="flex flex-col gap-4 rounded-2xl border border-[var(--foreground)]/8 bg-white/4 p-6 backdrop-blur-sm"
            >
              <div className="flex gap-0.5">
                {Array.from({ length: review.rating }).map((_, i) => (
                  <Star key={i} size={15} className="fill-[var(--accent)] text-[var(--accent)]" />
                ))}
              </div>
              <p className="flex-1 text-sm leading-relaxed text-[var(--foreground)]/80">
                &ldquo;{review.body}&rdquo;
              </p>
              <div className="flex items-center gap-3 border-t border-[var(--foreground)]/8 pt-4">
                <Image
                  src={review.avatar}
                  alt={review.name}
                  width={40}
                  height={40}
                  className="h-10 w-10 rounded-full object-cover"
                />
                <div>
                  <p className="text-sm font-semibold text-[var(--foreground)]">{review.name}</p>
                  <p className="text-xs text-[var(--foreground)]/50">
                    {review.role} · {review.company}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
